import { useMemo } from "react";
import { useQuery } from "@apollo/react-hooks";
import { GET_MEDICAL_STAFF } from "@dhn/pages/medical-staff/queries";

const EMPTY_VALUE = "-";

const normalizeText = (value) => (value || "").toString().trim();

const joinText = (values, separator = " ") =>
  values.map(normalizeText).filter(Boolean).join(separator);

// Datos del médico en el consultorio y sucursal activos. La relación
// consultorio/sucursal/perfil sale de `dataDoctor`; los datos personales,
// especialidades y ARS se consultan del personal médico.
export default function useDoctorProfileDetails({ isOpen, dataDoctor, user }) {
  const medicalStaffId = Number(dataDoctor?.medicalStaffId || 0);

  const { data, loading } = useQuery(GET_MEDICAL_STAFF, {
    skip: !isOpen || !medicalStaffId,
    fetchPolicy: "cache-and-network",
    variables: { where: { id: medicalStaffId } },
  });

  return useMemo(() => {
    const staff = data?.medicalStaff || {};
    const fullName =
      joinText([staff.firstName, staff.middleName, staff.lastName, staff.lastName2]) ||
      normalizeText(dataDoctor?.fullName) ||
      joinText([user?.firstName, user?.lastName]) ||
      EMPTY_VALUE;

    return {
      loading,
      fullName,
      personalFields: [
        { label: "Cédula", value: staff.identificationCard },
        { label: "Exequátur", value: staff.exequatur || dataDoctor?.exequatur },
        { label: "Usuario", value: staff.username || user?.username },
        { label: "Correo", value: staff.email || dataDoctor?.email || user?.email },
        {
          label: "Teléfonos",
          value: joinText([staff.phone, staff.cellphone, staff.phone2], " · "),
        },
      ].map((field) => ({ ...field, value: normalizeText(field.value) || EMPTY_VALUE })),
      address: normalizeText(staff.address) || EMPTY_VALUE,
      officeFields: [
        { label: "Consultorio", value: dataDoctor?.centerName },
        { label: "Sucursal", value: dataDoctor?.branchOfficeName },
        { label: "Perfil", value: dataDoctor?.profileName },
      ].map((field) => ({ ...field, value: normalizeText(field.value) || EMPTY_VALUE })),
      isAdmin: Boolean(dataDoctor?.isAdmin),
      isEnabled: dataDoctor?.enabled !== false,
      specialities: (staff.specialitiesByMedical?.length
        ? staff.specialitiesByMedical.map((item) => item?.speciality?.name)
        : normalizeText(dataDoctor?.nameSpeciality).split(",")
      )
        .map(normalizeText)
        .filter(Boolean)
        .map((name) => ({ name })),
      arses: (staff.arses || []).map((item) => ({
        id: item?.id,
        name: normalizeText(item?.ars?.name) || EMPTY_VALUE,
        code: normalizeText(item?.code) || EMPTY_VALUE,
      })),
    };
  }, [data, loading, dataDoctor, user]);
}
